// src/components/aulas/AutoAssignDialog.tsx
import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Button,
  LinearProgress,
  Typography,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";
import {
  useStartAutoAssignJob,
  useAutoAssignJobStatus,
  useCancelAutoAssignJob,
} from "../../api/aulas";

type Props = {
  open: boolean;
  onClose: () => void;
  defaultCentroId?: number;
};

export default function AutoAssignDialog({ open, onClose, defaultCentroId }: Props) {
  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
  const [desde, setDesde] = useState<string>(today);
  const [hasta, setHasta] = useState<string>("");

  // Si viene defaultCentroId, bloqueamos a ese centro para evitar cruces
  const forcedCentroId = defaultCentroId ?? null;
  const [centroId, setCentroId] = useState<number | "">(forcedCentroId ?? "");

  const [concurrency, setConcurrency] = useState<number>(6);
  const [pageSize, setPageSize] = useState<number>(500);

  const [jobId, setJobId] = useState<string | null>(null);
  const startJob = useStartAutoAssignJob();
  const cancelJob = useCancelAutoAssignJob();
  const { data: status } = useAutoAssignJobStatus(jobId);

  const running = status?.status === "RUNNING" || status?.status === "PENDING";
  const finished =
    status?.status === "DONE" || status?.status === "ERROR" || status?.status === "CANCELLED";
  const progress = status?.total
    ? Math.min(100, Math.round((status.processed / status.total) * 100))
    : 0;

  async function handleRun() {
    if (!desde) return;
    const body: any = {
      desde,
      concurrency,
      pageSize,
    };
    if (hasta) body.hasta = hasta;

    // Política: si hay centro forzado, lo mandamos siempre.
    // Si no hay centro forzado pero el usuario pone un centro manualmente, también lo enviamos.
    if (forcedCentroId) body.centroId = forcedCentroId;
    else if (centroId) body.centroId = Number(centroId);

    const res = await startJob.mutateAsync(body);
    setJobId(res.jobId);
  }

  async function handleCancel() {
    if (jobId) await cancelJob.mutateAsync(jobId);
  }

  function handleClose() {
    if (running && !window.confirm("Hay un proceso en marcha. ¿Cancelar y cerrar?")) return;
    if (running && jobId) cancelJob.mutate(jobId);
    setJobId(null);
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto-asignación de aulas (Background)</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {forcedCentroId && (
            <Alert severity="info">
              Este diálogo está <b>restringido al centro #{forcedCentroId}</b> para evitar cruces.
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              type="date"
              label="Desde"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={running}
            />
            <TextField
              type="date"
              label="Hasta (opcional)"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={running}
            />
          </Stack>

          {/* Centro: sólo editable si no viene forzado desde fuera */}
          <TextField
            type="number"
            label="Centro ID"
            value={centroId}
            onChange={(e) => setCentroId(e.target.value ? Number(e.target.value) : "")}
            disabled={running || !!forcedCentroId}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              type="number"
              label="Concurrency"
              value={concurrency}
              onChange={(e) => setConcurrency(Math.max(1, Number(e.target.value)))}
              disabled={running}
              fullWidth
            />
            <TextField
              type="number"
              label="Page size"
              value={pageSize}
              onChange={(e) => setPageSize(Math.max(50, Number(e.target.value)))}
              disabled={running}
              fullWidth
            />
          </Stack>

          {/* Progreso */}
          {jobId && (
            <Stack spacing={1}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="body2">
                Job: <strong>{status?.jobId}</strong> — Estado:{" "}
                <strong>{status?.status ?? "—"}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {status?.processed ?? 0}/{status?.total ?? 0} · OK: {status?.ok ?? 0} · Errores:{" "}
                {status?.errors ?? 0}
                {status?.lastError ? ` · Último error: ${status.lastError}` : ""}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cerrar</Button>
        {!running && (
          <Button variant="contained" onClick={handleRun}>
            Lanzar
          </Button>
        )}
        {running && (
          <Button color="error" variant="contained" onClick={handleCancel}>
            Cancelar
          </Button>
        )}
        {finished && <Button onClick={() => setJobId(null)}>Limpiar estado</Button>}
      </DialogActions>
    </Dialog>
  );
}
